import React, { useEffect, useMemo, useRef, useState } from "react";
import { createChart, CandlestickSeries } from "lightweight-charts";
import { runMasterPipeline } from "../utils/engine/masterPipeline";
import { ZONE_PROXIMITY_TEST_PIPS } from "../utils/engine/zoneShared";
import {
  evaluateZoneOutcome,
  patchZoneOutcomeResult,
  resetZoneOutcomeTracking,
  withZoneOutcomeAnalysis,
} from "../utils/engine/zoneOutcomeTracker";
import { resetOverlays } from "../utils/chartOverlayEngine";
import zoneOutcomePersistenceService, {
  buildPersistencePatch,
} from "../services/zoneOutcomePersistenceService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SESSION_TOKEN_KEY = "spike_auth_token";
function getApiWsUrl() {
  const base = `${API_BASE_URL?.replace(/^http/i, "ws")}/market-stream`;
  const token = window.localStorage.getItem(SESSION_TOKEN_KEY);
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}
const SYMBOLS = {
  BOOM: ["Boom 1000", "Boom 900", "Boom 600", "Boom 500", "Boom300N"],
  CRASH: ["Crash 1000", "Crash 900", "Crash 600", "Crash 500", "Crash300N"],
};

const TIMEFRAMES = [
  { label: "1m", value: 60 },
  { label: "3m", value: 180 },
  { label: "5m", value: 300 },
  { label: "15m", value: 900 },
  { label: "30m", value: 1800 },
  { label: "1h", value: 3600 },
  { label: "4h", value: 14400 },
];
const HTF_TIMEFRAMES = [300, 900, 1800];
const STRATEGIES = [
  { label: "Smart Zone", value: "SMART_ZONE" },
  { label: "MSS Hunter", value: "MSS_HUNTER" },
];
const MAX_CHART_CANDLES = 1500;
const MIN_SL_PIPS = 5;
const MAX_SL_PIPS = 100;
const EMIT_FALLBACK_DELAY_MS = 200;
const LIVE_STREAM_STALE_MS = 15000;
const STREAM_HEALTHCHECK_MS = 5000;
const STREAM_RECONNECT_DELAY_MS = 1000;

function detectMarketFromSymbol(symbol) {
  return /crash/i.test(symbol || "") ? "CRASH" : "BOOM";
}

function getInitialMarket() {
  const storedMarket = window.localStorage.getItem("spike-market");

  if (storedMarket === "BOOM" || storedMarket === "CRASH") {
    return storedMarket;
  }

  const storedSymbol = window.localStorage.getItem("spike-symbol");
  return detectMarketFromSymbol(storedSymbol);
}

function getInitialSymbol(market) {
  const storedSymbol = window.localStorage.getItem("spike-symbol");

  if (storedSymbol && SYMBOLS[market]?.includes(storedSymbol)) {
    return storedSymbol;
  }

  return SYMBOLS[market][0];
}

function getInitialTimeframe() {
  const storedTimeframe = Number(window.localStorage.getItem("spike-timeframe"));

  if (TIMEFRAMES.some((item) => item.value === storedTimeframe)) {
    return storedTimeframe;
  }

  return 300;
}

function getUserSettingsKey(user, settingName) {
  const userKey =
    user?.id ||
    user?.email ||
    user?.cr_number ||
    user?.mt5_number ||
    "guest";

  return `spike-user:${userKey}:${settingName}`;
}

function getChartHeight() {
  const viewportHeight = window.innerHeight || 800;

  if (viewportHeight < 700) {
    return Math.max(360, Math.floor(viewportHeight * 0.7));
  }

  return Math.max(520, Math.floor(viewportHeight * 0.82));
}

function getChartApiSnapshot(chart, candleSeries, container) {
  if (!chart || !candleSeries || !container) {
    return null;
  }

  return {
    chart,
    candleSeries,
    container,
  };
}

function publishChartApi(onChartReady, chart, candleSeries, container) {
  onChartReady?.(getChartApiSnapshot(chart, candleSeries, container));
}

function isNearRealtime(chart) {
  if (!chart) return true;

  return chart.timeScale().scrollPosition() > -3;
}

function toClosedHistory(candles) {
  return normalizeHistoryCandles(candles).map((candle) => ({
    ...candle,
    isClosed: candle.isClosed !== false,
  }));
}

function normalizeCandle(candle, timeframeSeconds) {
  if (!candle) {
    return null;
  }

  const rawTime = Number(candle.time);
  const open = Number(candle.open);
  const high = Number(candle.high);
  const low = Number(candle.low);
  const close = Number(candle.close);

  if (
    !Number.isFinite(rawTime) ||
    !Number.isFinite(open) ||
    !Number.isFinite(high) ||
    !Number.isFinite(low) ||
    !Number.isFinite(close)
  ) {
    return null;
  }

  const safeTimeframe =
    Number.isFinite(Number(timeframeSeconds)) && Number(timeframeSeconds) > 0
      ? Number(timeframeSeconds)
      : null;
  const time = safeTimeframe
    ? Math.floor(rawTime / safeTimeframe) * safeTimeframe
    : rawTime;
  const normalizedHigh = Math.max(open, high, low, close);
  const normalizedLow = Math.min(open, high, low, close);

  return {
    ...candle,
    time,
    open,
    high: normalizedHigh,
    low: normalizedLow,
    close,
    isClosed: candle.isClosed !== false,
  };
}

function normalizeHistoryCandles(candles) {
  return (candles || [])
    .map((candle) => normalizeCandle(candle, candle?.timeframeSeconds))
    .filter(Boolean);
}

function splitClosedAndActiveCandles(candles) {
  const normalizedCandles = normalizeHistoryCandles(candles);
  const lastCandle = normalizedCandles[normalizedCandles.length - 1] || null;

  if (lastCandle?.isClosed === false) {
    return {
      closedCandles: normalizedCandles.slice(0, -1),
      activeCandle: lastCandle,
    };
  }

  return {
    closedCandles: normalizedCandles,
    activeCandle: null,
  };
}

function createHtfStreamState() {
  return HTF_TIMEFRAMES.reduce((state, htfTimeframe) => {
    state[htfTimeframe] = {
      closedCandles: [],
      activeCandle: null,
    };
    return state;
  }, {});
}

function upsertLiveCandle(candles, incomingCandle) {
  const normalizedIncomingCandle = normalizeCandle(
    incomingCandle,
    incomingCandle?.timeframeSeconds
  );

  if (!normalizedIncomingCandle) {
    return candles;
  }

  if (!candles.length) {
    return [{ ...normalizedIncomingCandle, isClosed: false }];
  }

  const nextCandles = candles.slice();
  const lastCandle = nextCandles[nextCandles.length - 1];

  if (normalizedIncomingCandle.time === lastCandle.time) {
    nextCandles[nextCandles.length - 1] = {
      ...lastCandle,
      ...normalizedIncomingCandle,
      isClosed: false,
    };

    return nextCandles;
  }

  if (normalizedIncomingCandle.time > lastCandle.time) {
    nextCandles[nextCandles.length - 1] = {
      ...lastCandle,
      isClosed: true,
    };

    nextCandles.push({
      ...normalizedIncomingCandle,
      isClosed: false,
    });

    return nextCandles.slice(-MAX_CHART_CANDLES);
  }

  return candles;
}

function mergeHistoryWithPendingLive(historyCandles, pendingLiveCandles) {
  if (!pendingLiveCandles.length) {
    return historyCandles;
  }

  return pendingLiveCandles.reduce(
    (mergedCandles, candle) => upsertLiveCandle(mergedCandles, candle),
    historyCandles
  );
}

function formatCountdown(totalSeconds) {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function ToolbarIcon({ kind }) {
  const commonProps = {
    className: "toolbar-icon-svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  if (kind === "market") {
    return (
      <svg {...commonProps}>
        <path d="M4 16l4-5 4 3 6-8" />
        <path d="M18 6h0" />
        <path d="M4 20h16" />
      </svg>
    );
  }

  if (kind === "symbol") {
    return (
      <svg {...commonProps}>
        <rect x="3.5" y="5" width="17" height="14" rx="3" />
        <path d="M7 14h4" />
        <path d="M13 10h4" />
        <path d="M13 14h4" />
      </svg>
    );
  }

  if (kind === "timeframe") {
    return (
      <svg {...commonProps}>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5v5l3 2" />
      </svg>
    );
  }

  if (kind === "strategy") {
    return (
      <svg {...commonProps}>
        <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
        <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
        <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
        <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
      </svg>
    );
  }

  if (kind === "risk") {
    return (
      <svg {...commonProps}>
        <path d="M12 3l8.5 15H3.5L12 3z" />
        <path d="M12 9v4" />
        <path d="M12 16h0" />
      </svg>
    );
  }

  if (kind === "alert") {
    return (
      <svg {...commonProps}>
        <path d="M12 4.5a4.5 4.5 0 0 0-4.5 4.5v2.2c0 .7-.2 1.3-.6 1.9L5.8 15h12.4l-1.1-1.9a3.7 3.7 0 0 1-.6-1.9V9A4.5 4.5 0 0 0 12 4.5z" />
        <path d="M10 18a2.2 2.2 0 0 0 4 0" />
      </svg>
    );
  }

  if (kind === "status") {
    return (
      <svg {...commonProps}>
        <path d="M5 16l4-4 3 2 7-7" />
        <path d="M16 7h3v3" />
      </svg>
    );
  }

  if (kind === "user") {
    return (
      <svg {...commonProps}>
        <circle cx="12" cy="8.5" r="3.5" />
        <path d="M5 19c1.7-3 4.2-4.5 7-4.5s5.3 1.5 7 4.5" />
      </svg>
    );
  }

  return null;
}

export default function TradingViewChart({
  user,
  onLogout,
  onNavigateResults,
  onNavigateRadar,
  onNavigateAutoTrader,
  onCandleUpdate,
  onContextUpdate,
  onAnalysisUpdate,
  onStructureUpdate,
  onChartReady,
  alertStatus = null,
  onEnableAlertDelivery,
  availableZones = [],
  availableOrderBlocks = [],
  selectedZoneId = "ALL",
  selectedOrderBlockId = "ALL",
  onZoneFilterChange,
  onOrderBlockFilterChange,
  showAllZones = false,
  onShowAllZonesChange,
  strategy = "SMART_ZONE",
  onStrategyChange,
}) {
  const initialMarket = getInitialMarket();
  const [market, setMarket] = useState(initialMarket);
  const [symbol, setSymbol] = useState(() => getInitialSymbol(initialMarket));
  const [timeframe, setTimeframe] = useState(getInitialTimeframe);
  const [status, setStatus] = useState("Loading...");
  const [slPips, setSlPips] = useState(ZONE_PROXIMITY_TEST_PIPS);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [marketSnapshot, setMarketSnapshot] = useState({
    price: null,
    candleTime: null,
    isClosed: true,
  });

  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const socketRef = useRef(null);
  const htfSocketsRef = useRef(new Map());
  const candlesRef = useRef([]);
  const htfStreamStateRef = useRef(createHtfStreamState());
  const historyAbortRef = useRef(null);
  const loadVersionRef = useRef(0);
  const autoFollowRef = useRef(true);
  const previousResultRef = useRef(null);
  const latestAnalysisRef = useRef(null);
  const pendingLiveCandlesRef = useRef([]);
  const pendingEmitCandlesRef = useRef(null);
  const emitFrameRef = useRef(null);
  const emitTimeoutRef = useRef(null);
  const viewportFrameRef = useRef(null);
  const streamHealthcheckRef = useRef(null);
  const liveReconnectTimeoutRef = useRef(null);
  const htfReconnectTimeoutsRef = useRef(new Map());
  const latestLiveTickAtRef = useRef(0);
  const isUnmountedRef = useRef(false);
  const availableSymbols = useMemo(() => SYMBOLS[market] || [], [market]);

  useEffect(() => {
    const storageKey = getUserSettingsKey(user, "sl-pips");
    const storedValue = Number(window.localStorage.getItem(storageKey));

    if (Number.isFinite(storedValue) && storedValue >= MIN_SL_PIPS && storedValue <= MAX_SL_PIPS) {
      setSlPips(storedValue);
      return;
    }

    setSlPips(ZONE_PROXIMITY_TEST_PIPS);
  }, [user]);

  useEffect(() => {
    const storageKey = getUserSettingsKey(user, "sl-pips");
    window.localStorage.setItem(storageKey, String(slPips));
  }, [user, slPips]);

  useEffect(() => {
    window.localStorage.setItem("spike-market", market);
  }, [market]);

  useEffect(() => {
    window.localStorage.setItem("spike-symbol", symbol);
    window.localStorage.setItem("spike-timeframe", String(timeframe));
    onContextUpdate?.({ symbol, timeframe });
  }, [symbol, timeframe, onContextUpdate]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setClockNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const handleVisibleRangeChange = () => {
      autoFollowRef.current = isNearRealtime(chartRef.current);
    };
    const container = containerRef.current;

    chartRef.current = createChart(container, {
      width: container.clientWidth,
      height: getChartHeight(),
      layout: {
        background: { color: "#0f172a" },
        textColor: "#e5e7eb",
      },
      grid: {
        vertLines: { color: "#1f2933" },
        horzLines: { color: "#1f2933" },
      },
      timeScale: {
        timeVisible: true,
        rightOffset: 10,
        barSpacing: 9,
        minBarSpacing: 0.5,
        fixLeftEdge: false,
        lockVisibleTimeRangeOnResize: false,
        borderColor: "#334155",
      },
      rightPriceScale: {
        borderColor: "#334155",
        scaleMargins: {
          top: 0.12,
          bottom: 0.12,
        },
      },
      handleScroll: true,
      handleScale: true,
      crosshair: {
        vertLine: {
          color: "rgba(148, 163, 184, 0.35)",
          width: 1,
        },
        horzLine: {
          color: "rgba(148, 163, 184, 0.25)",
          width: 1,
        },
      },
    });

    candleSeriesRef.current = chartRef.current.addSeries(CandlestickSeries, {
      lastValueVisible: false,
    });
    publishChartApi(
      onChartReady,
      chartRef.current,
      candleSeriesRef.current,
      container
    );

    const resizeObserver = new ResizeObserver(() => {
      if (!chartRef.current || !container) return;

      chartRef.current.applyOptions({
        width: container.clientWidth,
        height: getChartHeight(),
      });
    });

    resizeObserver.observe(container);
    chartRef.current.timeScale().subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
    const htfSockets = htfSocketsRef.current;
    const reconnectTimeouts = htfReconnectTimeoutsRef.current;
    return () => {
      isUnmountedRef.current = true;
      const chart = chartRef.current;
      const candleSeries = candleSeriesRef.current;
      historyAbortRef.current?.abort();
      socketRef.current?.close();
      htfSockets.forEach((socket) => {
        socket.close();
      });
      htfSockets.clear();
      clearPendingEmit();
      if (viewportFrameRef.current) {
        window.cancelAnimationFrame(viewportFrameRef.current);
        viewportFrameRef.current = null;
      }
      window.clearInterval(streamHealthcheckRef.current);
      streamHealthcheckRef.current = null;
      window.clearTimeout(liveReconnectTimeoutRef.current);
      liveReconnectTimeoutRef.current = null;
      reconnectTimeouts.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      reconnectTimeouts.clear();
      chart
        ?.timeScale()
        .unsubscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
      resizeObserver.disconnect();
      onCandleUpdate?.([]);
      onAnalysisUpdate?.(null);
      onStructureUpdate?.(null);
      onChartReady?.(null);
      candleSeriesRef.current = null;
      chartRef.current = null;
      resetOverlays(getChartApiSnapshot(chart, candleSeries, container));
      chart?.remove();
      onChartReady?.(null);
    };
  }, [onAnalysisUpdate, onCandleUpdate, onChartReady, onStructureUpdate]);

  useEffect(() => {
    void loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe]);

  function buildAnalysis(nextCandles) {
    const pipelineAnalysis = runMasterPipeline({
      candlesM1: nextCandles,
      candlesM5: getLiveHtfCandles(300),
      candlesM15: getLiveHtfCandles(900),
      candlesM30: getLiveHtfCandles(1800),
      market: detectMarketFromSymbol(symbol),
      symbol,
      timeframe,
      prevResult: previousResultRef.current,
      slPips,
    });

    const zoneOutcome = evaluateZoneOutcome({
      symbol,
      timeframe,
      market: detectMarketFromSymbol(symbol),
      candles: nextCandles,
      analysis: pipelineAnalysis,
      options: {
        approachPips: ZONE_PROXIMITY_TEST_PIPS,
        spikeMovePips: Math.max(8, Math.min(15, Number(slPips) || 10)),
        failureBufferPips: Number(slPips) || ZONE_PROXIMITY_TEST_PIPS,
      },
    });
    const nextAnalysis = withZoneOutcomeAnalysis(pipelineAnalysis, zoneOutcome);

    previousResultRef.current = pipelineAnalysis?.result ?? null;
    latestAnalysisRef.current = nextAnalysis ?? null;
    return nextAnalysis;
  }

  function updatePersistenceState(resultId, patch) {
    if (!resultId || previousResultRef.current?.id !== resultId) {
      return;
    }

    previousResultRef.current = {
      ...previousResultRef.current,
      ...patch,
    };

    if (latestAnalysisRef.current?.result?.id === resultId) {
      latestAnalysisRef.current = {
        ...latestAnalysisRef.current,
        result: {
          ...latestAnalysisRef.current.result,
          ...patch,
        },
      };

      onAnalysisUpdate?.(latestAnalysisRef.current);
    }
  }

  function enqueueClosedResult(result) {
    if (!result?.closed || !result?.id) {
      return;
    }

    zoneOutcomePersistenceService.enqueue(result, {
      symbol,
      timeframe,
    });
  }

  function updateZoneOutcomePersistenceState(resultId, patch) {
    patchZoneOutcomeResult({
      symbol,
      timeframe,
      resultId,
      patch,
    });

    if (!resultId || latestAnalysisRef.current?.zoneOutcome?.result?.id !== resultId) {
      return;
    }

    latestAnalysisRef.current = {
      ...latestAnalysisRef.current,
      zoneOutcome: {
        ...latestAnalysisRef.current.zoneOutcome,
        result: {
          ...latestAnalysisRef.current.zoneOutcome.result,
          ...patch,
        },
      },
    };

    onAnalysisUpdate?.(latestAnalysisRef.current);
  }

  function enqueueZoneOutcomeResult(result) {
    if (!result?.id) {
      return;
    }

    zoneOutcomePersistenceService.enqueue(result, {
      symbol,
      timeframe,
    });
  }

  useEffect(() => {
    if (!candlesRef.current.length) {
      return;
    }

    emitState(candlesRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slPips]);

  useEffect(() => {
    return zoneOutcomePersistenceService.subscribe((snapshot) => {
      const patch = buildPersistencePatch(snapshot);

      if (!patch) {
        return;
      }

      updatePersistenceState(snapshot.id, patch);
      updateZoneOutcomePersistenceState(snapshot.id, patch);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe]);

  function emitState(nextCandles) {
    candlesRef.current = nextCandles;
    const latestCandle = nextCandles[nextCandles.length - 1] || null;
    setMarketSnapshot({
      price: latestCandle?.close ?? null,
      candleTime: latestCandle?.time ?? null,
      isClosed: latestCandle?.isClosed !== false,
    });
    onCandleUpdate?.(nextCandles);
    const nextAnalysis = buildAnalysis(nextCandles);
    enqueueClosedResult(nextAnalysis?.result);
    enqueueZoneOutcomeResult(nextAnalysis?.zoneOutcome?.result);
    const analysisSnapshot = latestAnalysisRef.current || nextAnalysis;

    onAnalysisUpdate?.(analysisSnapshot);
    onStructureUpdate?.(analysisSnapshot?.structure || null);
  }

  function clearPendingEmit() {
    if (emitFrameRef.current) {
      window.cancelAnimationFrame(emitFrameRef.current);
      emitFrameRef.current = null;
    }

    if (emitTimeoutRef.current) {
      window.clearTimeout(emitTimeoutRef.current);
      emitTimeoutRef.current = null;
    }
  }

  function flushPendingEmit() {
    clearPendingEmit();

    if (!pendingEmitCandlesRef.current) {
      return;
    }

    const latestCandles = pendingEmitCandlesRef.current;
    pendingEmitCandlesRef.current = null;
    emitState(latestCandles);
  }

  function scheduleEmitState(nextCandles) {
    pendingEmitCandlesRef.current = nextCandles;

    if (!emitFrameRef.current && typeof window.requestAnimationFrame === "function") {
      emitFrameRef.current = window.requestAnimationFrame(() => {
        flushPendingEmit();
      });
    }

    if (!emitTimeoutRef.current) {
      emitTimeoutRef.current = window.setTimeout(() => {
        flushPendingEmit();
      }, EMIT_FALLBACK_DELAY_MS);
    }
  }

  function focusLatestPrice() {
    if (!chartRef.current || !containerRef.current || !candlesRef.current.length) {
      return;
    }

    chartRef.current.applyOptions({
      width: containerRef.current.clientWidth,
      height: getChartHeight(),
    });

    const syncViewport = () => {
      chartRef.current?.timeScale().fitContent();
      chartRef.current?.timeScale().scrollToRealTime();
    };

    syncViewport();

    if (typeof window.requestAnimationFrame === "function") {
      if (viewportFrameRef.current) {
        window.cancelAnimationFrame(viewportFrameRef.current);
      }

      viewportFrameRef.current = window.requestAnimationFrame(() => {
        viewportFrameRef.current = null;
        syncViewport();
      });
    }
  }

  function clearChartView() {
    resetOverlays(
      getChartApiSnapshot(
        chartRef.current,
        candleSeriesRef.current,
        containerRef.current
      )
    );
    candleSeriesRef.current?.setData([]);
    chartRef.current?.timeScale().fitContent();
  }

  function prepareForContextChange() {
    resetZoneOutcomeTracking({ symbol, timeframe });
    clearCurrentFeed();
    onChartReady?.(null);
    setStatus("Loading...");
    autoFollowRef.current = true;
    previousResultRef.current = null;
    latestAnalysisRef.current = null;
    pendingLiveCandlesRef.current = [];
    pendingEmitCandlesRef.current = null;
    candlesRef.current = [];
    clearChartView();
    onCandleUpdate?.([]);
    onAnalysisUpdate?.(null);
    onStructureUpdate?.(null);
    setMarketSnapshot({
      price: null,
      candleTime: null,
      isClosed: true,
    });
  }

  function clearCurrentFeed() {
    loadVersionRef.current += 1;
    historyAbortRef.current?.abort();
    historyAbortRef.current = null;
    socketRef.current?.close();
    socketRef.current = null;
    htfSocketsRef.current.forEach((socket) => {
      socket.close();
    });
    htfSocketsRef.current.clear();
    htfStreamStateRef.current = createHtfStreamState();
    pendingLiveCandlesRef.current = [];
    pendingEmitCandlesRef.current = null;
    clearPendingEmit();
    window.clearTimeout(liveReconnectTimeoutRef.current);
    liveReconnectTimeoutRef.current = null;
    htfReconnectTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    htfReconnectTimeoutsRef.current.clear();
    window.clearInterval(streamHealthcheckRef.current);
    streamHealthcheckRef.current = null;
    latestLiveTickAtRef.current = 0;
  }

  function getUniqueHistoryTimeframes() {
    return [...new Set([timeframe, ...HTF_TIMEFRAMES])];
  }

  async function fetchHistoryForTimeframe(nextTimeframe, signal) {
    const token = window.localStorage.getItem(SESSION_TOKEN_KEY);
    const response = await fetch(
      `${API_BASE_URL}/history?symbol=${encodeURIComponent(
        symbol
      )}&timeframe=${nextTimeframe}&includeStructure=1`,
      {
        signal,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );

    if (!response.ok) {
      throw new Error(
        `History fetch failed for ${nextTimeframe}: ${response.status}`
      );
    }

    const data = await response.json();
    const rawCandles = Array.isArray(data) ? data : data.candles || [];

    return rawCandles
      .map((candle) => normalizeCandle(candle, nextTimeframe))
      .filter(Boolean)
      .slice(-MAX_CHART_CANDLES);
  }

  function setHtfHistory(timeframeValue, candles) {
    const { closedCandles, activeCandle } = splitClosedAndActiveCandles(candles);
    htfStreamStateRef.current[timeframeValue] = {
      closedCandles: closedCandles.slice(-MAX_CHART_CANDLES),
      activeCandle,
    };
  }

  function getLiveHtfCandles(timeframeValue) {
    const htfState = htfStreamStateRef.current[timeframeValue];
    const closedCandles = htfState?.closedCandles || [];
    const activeCandle = htfState?.activeCandle;

    // Include the live HTF candle so global-context confidence reflects every incoming tick.
    return activeCandle ? [...closedCandles, activeCandle] : closedCandles;
  }

  function applyHtfLiveUpdate(timeframeValue, incomingCandle) {
    if (!HTF_TIMEFRAMES.includes(timeframeValue)) {
      return;
    }

    const currentState =
      htfStreamStateRef.current[timeframeValue] || {
        closedCandles: [],
        activeCandle: null,
      };
    let nextClosedCandles = currentState.closedCandles;
    let nextActiveCandle = currentState.activeCandle;
    let closedCandleCommitted = false;

    if (!nextActiveCandle) {
      nextActiveCandle = {
        ...incomingCandle,
        isClosed: false,
      };
    } else if (incomingCandle.time === nextActiveCandle.time) {
      nextActiveCandle = {
        ...nextActiveCandle,
        ...incomingCandle,
        isClosed: false,
      };
    } else if (incomingCandle.time > nextActiveCandle.time) {
      nextClosedCandles = [
        ...nextClosedCandles,
        {
          ...nextActiveCandle,
          isClosed: true,
        },
      ].slice(-MAX_CHART_CANDLES);
      nextActiveCandle = {
        ...incomingCandle,
        isClosed: false,
      };
      closedCandleCommitted = true;
    }

    htfStreamStateRef.current[timeframeValue] = {
      closedCandles: nextClosedCandles,
      activeCandle: nextActiveCandle,
    };

    if ((closedCandleCommitted || nextActiveCandle) && candlesRef.current.length) {
      scheduleEmitState(candlesRef.current);
    }
  }

  function scheduleLiveReconnect(loadVersion) {
    if (liveReconnectTimeoutRef.current || loadVersion !== loadVersionRef.current) {
      return;
    }

    liveReconnectTimeoutRef.current = window.setTimeout(() => {
      liveReconnectTimeoutRef.current = null;

      if (
        loadVersion !== loadVersionRef.current ||
        socketRef.current?.readyState === WebSocket.OPEN ||
        socketRef.current?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }

      startLive(loadVersion);
    }, STREAM_RECONNECT_DELAY_MS);
  }

  function scheduleHtfReconnect(loadVersion, htfTimeframe) {
    if (
      htfReconnectTimeoutsRef.current.has(htfTimeframe) ||
      loadVersion !== loadVersionRef.current
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      htfReconnectTimeoutsRef.current.delete(htfTimeframe);

      if (loadVersion !== loadVersionRef.current) {
        return;
      }

      const existingSocket = htfSocketsRef.current.get(htfTimeframe);
      if (
        existingSocket?.readyState === WebSocket.OPEN ||
        existingSocket?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }

      startHtfLive(loadVersion, htfTimeframe);
    }, STREAM_RECONNECT_DELAY_MS);

    htfReconnectTimeoutsRef.current.set(htfTimeframe, timeoutId);
  }

  function startStreamHealthcheck(loadVersion) {
    window.clearInterval(streamHealthcheckRef.current);

    streamHealthcheckRef.current = window.setInterval(() => {
      if (loadVersion !== loadVersionRef.current) {
        return;
      }

      const lastTickAt = latestLiveTickAtRef.current;
      const liveSocketState = socketRef.current?.readyState;
      const streamIsStale =
        lastTickAt > 0 && Date.now() - lastTickAt > LIVE_STREAM_STALE_MS;

      if (
        liveSocketState === WebSocket.CLOSED ||
        liveSocketState === WebSocket.CLOSING ||
        streamIsStale
      ) {
        if (
          socketRef.current &&
          socketRef.current.readyState !== WebSocket.CLOSED
        ) {
          socketRef.current.close();
        }
        scheduleLiveReconnect(loadVersion);
      }
    }, STREAM_HEALTHCHECK_MS);
  }

  async function loadHistory() {
    const loadVersion = loadVersionRef.current + 1;
    loadVersionRef.current = loadVersion;

    clearCurrentFeed();
    loadVersionRef.current = loadVersion;

    onChartReady?.(null);
    setStatus("Loading...");
    autoFollowRef.current = true;
    resetZoneOutcomeTracking({ symbol, timeframe });
    previousResultRef.current = null;
    latestAnalysisRef.current = null;
    htfStreamStateRef.current = createHtfStreamState();
    pendingLiveCandlesRef.current = [];
    pendingEmitCandlesRef.current = null;
    candlesRef.current = [];
    clearChartView();
    onCandleUpdate?.([]);
    onAnalysisUpdate?.(null);
    onStructureUpdate?.(null);
    setMarketSnapshot({
      price: null,
      candleTime: null,
      isClosed: true,
    });

    historyAbortRef.current = new AbortController();

    try {
      const historyTimeframes = getUniqueHistoryTimeframes();
      const historyEntries = await Promise.all(
        historyTimeframes.map(async (historyTimeframe) => [
          historyTimeframe,
          await fetchHistoryForTimeframe(
            historyTimeframe,
            historyAbortRef.current.signal
          ),
        ])
      );
      const historyByTimeframe = Object.fromEntries(historyEntries);

      if (loadVersion !== loadVersionRef.current) {
        return;
      }

      HTF_TIMEFRAMES.forEach((htfTimeframe) => {
        const htfCandles = historyByTimeframe[htfTimeframe];

        if (htfCandles) {
          setHtfHistory(htfTimeframe, htfCandles);
        }
      });

      const historyCandles = toClosedHistory(
        historyByTimeframe[timeframe] || []
      ).slice(-MAX_CHART_CANDLES);
      const mergedCandles = mergeHistoryWithPendingLive(
        historyCandles,
        pendingLiveCandlesRef.current
      ).slice(-MAX_CHART_CANDLES);
      pendingLiveCandlesRef.current = [];

      if (!mergedCandles.length) {
        console.error("[TradingViewChart] loadHistory returned no candles, retrying...");
        setStatus("Retrying...");
        window.setTimeout(() => {
          if (loadVersion === loadVersionRef.current) {
            void loadHistory();
          }
        }, STREAM_RECONNECT_DELAY_MS);
        return;
      }

      candleSeriesRef.current?.setData(mergedCandles);
      emitState(mergedCandles);
      publishChartApi(
        onChartReady,
        chartRef.current,
        candleSeriesRef.current,
        containerRef.current
      );
      focusLatestPrice();
      startLive(loadVersion);
      HTF_TIMEFRAMES.filter((htfTimeframe) => htfTimeframe !== timeframe).forEach(
        (htfTimeframe) => {
          startHtfLive(loadVersion, htfTimeframe);
        }
      );
      setStatus("Live");
      startStreamHealthcheck(loadVersion);
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }

      console.error("[TradingViewChart] loadHistory failed:", error);
      setStatus("Retrying...");
      window.setTimeout(() => {
        if (loadVersion === loadVersionRef.current) {
          void loadHistory();
        }
      }, STREAM_RECONNECT_DELAY_MS);
    }
  }

  function startLive(loadVersion) {
    if (
      socketRef.current?.readyState === WebSocket.OPEN ||
      socketRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    const liveSocket = new WebSocket(getApiWsUrl());
    socketRef.current = liveSocket;

    liveSocket.onopen = () => {
      if (isUnmountedRef.current || loadVersion !== loadVersionRef.current) {
        liveSocket.close();
        return;
      }

      window.clearTimeout(liveReconnectTimeoutRef.current);
      liveReconnectTimeoutRef.current = null;
      latestLiveTickAtRef.current = Date.now();
      liveSocket.send(JSON.stringify({ symbol, timeframe }));
      setStatus("Live");
    };

    liveSocket.onmessage = (event) => {
      if (isUnmountedRef.current || loadVersion !== loadVersionRef.current) {
        return;
      }

      const message = JSON.parse(event.data);
      if (!message.candle) return;
      const normalizedLiveCandle = normalizeCandle(message.candle, timeframe);

      if (!normalizedLiveCandle) {
        return;
      }

      latestLiveTickAtRef.current = Date.now();
      pendingLiveCandlesRef.current = upsertLiveCandle(
        pendingLiveCandlesRef.current,
        normalizedLiveCandle
      );
      applyHtfLiveUpdate(timeframe, normalizedLiveCandle);

      if (!candlesRef.current.length) {
        return;
      }

      const nextCandles = upsertLiveCandle(
        candlesRef.current,
        normalizedLiveCandle
      );

      if (nextCandles === candlesRef.current) {
        return;
      }

      candleSeriesRef.current?.update(nextCandles[nextCandles.length - 1]);
      scheduleEmitState(nextCandles);

      if (autoFollowRef.current) {
        chartRef.current?.timeScale().scrollToRealTime();
      }
    };

    liveSocket.onerror = () => {
      if (isUnmountedRef.current || loadVersion !== loadVersionRef.current) {
        return;
      }

      setStatus("Reconnecting...");
    };

    liveSocket.onclose = () => {
      if (isUnmountedRef.current) {
        return;
      }

      if (loadVersion === loadVersionRef.current) {
        setStatus("Reconnecting...");
        scheduleLiveReconnect(loadVersion);
      }
    };
  }

  function startHtfLive(loadVersion, htfTimeframe) {
    const existingSocket = htfSocketsRef.current.get(htfTimeframe);
    if (
      existingSocket?.readyState === WebSocket.OPEN ||
      existingSocket?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    const liveSocket = new WebSocket(getApiWsUrl());
    htfSocketsRef.current.set(htfTimeframe, liveSocket);

    liveSocket.onopen = () => {
      if (isUnmountedRef.current || loadVersion !== loadVersionRef.current) {
        liveSocket.close();
        return;
      }

      const timeoutId = htfReconnectTimeoutsRef.current.get(htfTimeframe);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        htfReconnectTimeoutsRef.current.delete(htfTimeframe);
      }
      liveSocket.send(JSON.stringify({ symbol, timeframe: htfTimeframe }));
    };

    liveSocket.onmessage = (event) => {
      if (isUnmountedRef.current || loadVersion !== loadVersionRef.current) {
        return;
      }

      const message = JSON.parse(event.data);

      if (!message.candle) {
        return;
      }

      const normalizedLiveCandle = normalizeCandle(message.candle, htfTimeframe);

      if (!normalizedLiveCandle) {
        return;
      }

      applyHtfLiveUpdate(htfTimeframe, normalizedLiveCandle);
    };

    liveSocket.onclose = () => {
      htfSocketsRef.current.delete(htfTimeframe);
      if (isUnmountedRef.current) {
        return;
      }

      if (loadVersion === loadVersionRef.current) {
        scheduleHtfReconnect(loadVersion, htfTimeframe);
      }
    };
  }

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      flushPendingEmit();

      const liveSocketState = socketRef.current?.readyState;
      const liveStreamIsStale =
        latestLiveTickAtRef.current > 0 &&
        Date.now() - latestLiveTickAtRef.current > LIVE_STREAM_STALE_MS;

      if (
        liveStreamIsStale ||
        (liveSocketState !== WebSocket.OPEN &&
          liveSocketState !== WebSocket.CONNECTING)
      ) {
        void loadHistory();
        return;
      }

      HTF_TIMEFRAMES.filter((htfTimeframe) => htfTimeframe !== timeframe).forEach(
        (htfTimeframe) => {
          const htfSocket = htfSocketsRef.current.get(htfTimeframe);
          if (
            htfSocket?.readyState === WebSocket.OPEN ||
            htfSocket?.readyState === WebSocket.CONNECTING
          ) {
            return;
          }

          scheduleHtfReconnect(loadVersionRef.current, htfTimeframe);
        }
      );
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe]);

  function handleMarketChange(nextMarket) {
    prepareForContextChange();
    setMarket(nextMarket);
    setSymbol(getInitialSymbol(nextMarket));
  }

  const countdownMetrics = useMemo(() => {
    if (!marketSnapshot.candleTime || !timeframe) {
      return {
        remainingSeconds: 0,
        elapsedRatio: 0,
      };
    }

    const candleStartMs = Number(marketSnapshot.candleTime) * 1000;
    const candleDurationMs = Math.max(1, Number(timeframe)) * 1000;
    const elapsedMs = Math.max(0, clockNow - candleStartMs);
    const boundedElapsedMs = Math.min(candleDurationMs, elapsedMs);

    return {
      remainingSeconds: Math.ceil((candleDurationMs - boundedElapsedMs) / 1000),
      elapsedRatio: boundedElapsedMs / candleDurationMs,
    };
  }, [clockNow, marketSnapshot.candleTime, timeframe]);

  const livePriceBadge = useMemo(() => {
    const price = Number(marketSnapshot.price);
    const priceCoordinate = candleSeriesRef.current?.priceToCoordinate?.(price);

    if (!Number.isFinite(price) || !Number.isFinite(priceCoordinate)) {
      return null;
    }

    const priceText = price.toFixed(2);

    return {
      top: Math.max(12, priceCoordinate + 10),
      widthCh: Math.max(8.2, priceText.length + 1.4),
      priceText,
      countdownText: formatCountdown(countdownMetrics.remainingSeconds),
    };
  }, [countdownMetrics.remainingSeconds, marketSnapshot.price]);

  const alertDeliveryState = useMemo(() => {
    if (alertStatus?.pushEnabled) {
      return {
        label: "Push Ready",
        actionable: false,
      };
    }

    if (alertStatus?.permission === "denied") {
      return {
        label: "Blocked",
        actionable: false,
      };
    }

    if (alertStatus?.permission === "unsupported") {
      return {
        label: "Unsupported",
        actionable: false,
      };
    }

    return {
      label: "Enable Push",
      actionable: typeof onEnableAlertDelivery === "function",
    };
  }, [alertStatus, onEnableAlertDelivery]);

  return (
    <div className="chart-shell">
      <div className="chart-toolbar">
        <div className="dashboard-route-nav dashboard-route-nav-toolbar">
          <button type="button" className="dashboard-route-button dashboard-route-button-active">
            Live Workspace
          </button>
          <button type="button" className="dashboard-route-button" onClick={onNavigateResults}>
            Saved Results
          </button>
          <button type="button" className="dashboard-route-button" onClick={onNavigateRadar}>
            Radar
          </button>
          <button type="button" className="dashboard-route-button" onClick={onNavigateAutoTrader}>
            Auto Trader
          </button>
        </div>

        <div className="chart-toolbar-controls">
          <div className="toolbar-group toolbar-group-strategy">
            <span className="toolbar-icon"><ToolbarIcon kind="strategy" /></span>
            <label className="toolbar-field">
              <span className="toolbar-field-label">Strategy</span>
              <select
                className="chart-select chart-select-strategy"
                value={strategy}
                onChange={(event) => onStrategyChange?.(event.target.value)}
              >
                {STRATEGIES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="toolbar-group toolbar-group-market">
            <span className="toolbar-icon"><ToolbarIcon kind="market" /></span>
            <label className="toolbar-field">
              <span className="toolbar-field-label">Market</span>
              <select
                className="chart-select chart-select-market"
                value={market}
                onChange={(event) => handleMarketChange(event.target.value)}
              >
                <option value="BOOM">Boom</option>
                <option value="CRASH">Crash</option>
              </select>
            </label>
          </div>

          <div className="toolbar-group toolbar-group-symbol">
            <span className="toolbar-icon"><ToolbarIcon kind="symbol" /></span>
            <label className="toolbar-field">
              <span className="toolbar-field-label">Pair</span>
              <select
                className="chart-select"
                value={symbol}
                onChange={(event) => {
                  prepareForContextChange();
                  setSymbol(event.target.value);
                }}
              >
                {availableSymbols.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="toolbar-group toolbar-group-timeframe">
            <span className="toolbar-icon"><ToolbarIcon kind="timeframe" /></span>
            <label className="toolbar-field">
              <span className="toolbar-field-label">Timeframe</span>
              <select
                className="chart-select chart-select-timeframe"
                value={timeframe}
                onChange={(event) => {
                  prepareForContextChange();
                  setTimeframe(Number(event.target.value));
                }}
              >
                {TIMEFRAMES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="toolbar-group toolbar-group-sl">
            <span className="toolbar-icon"><ToolbarIcon kind="risk" /></span>
            <label className="toolbar-setting" htmlFor="sl-pips">
              <span className="toolbar-setting-label">Risk Buffer</span>
              <input
                id="sl-pips"
                className="toolbar-setting-input"
                type="number"
                min={MIN_SL_PIPS}
                max={MAX_SL_PIPS}
                step="1"
                value={slPips}
                onChange={(event) => {
                  const nextValue = Number(event.target.value);

                  if (!Number.isFinite(nextValue)) {
                    return;
                  }

                  setSlPips(Math.min(MAX_SL_PIPS, Math.max(MIN_SL_PIPS, nextValue)));
                }}
              />
            </label>
          </div>

          <div className="toolbar-group toolbar-group-alert">
            <span className="toolbar-icon"><ToolbarIcon kind="alert" /></span>
            <label className="toolbar-setting" htmlFor="alert-delivery">
              <span className="toolbar-setting-label">Alert Delivery</span>
              <button
                id="alert-delivery"
                type="button"
                className={`toolbar-setting-button${
                  alertDeliveryState.actionable ? " toolbar-setting-button-actionable" : ""
                }`}
                onClick={() => {
                  if (alertDeliveryState.actionable) {
                    onEnableAlertDelivery?.();
                  }
                }}
                disabled={!alertDeliveryState.actionable}
              >
                {alertDeliveryState.label}
              </button>
            </label>
          </div>

        </div>

        <div className="chart-toolbar-side">
          <div className="chart-status">
            <span className="toolbar-icon toolbar-icon-status">
              <ToolbarIcon kind="status" />
            </span>
            <strong className="chart-status-text">{status}</strong>
          </div>
          <div className="toolbar-session">
            <span className="toolbar-icon toolbar-icon-user">
              <ToolbarIcon kind="user" />
            </span>
            <div className="toolbar-session-copy">
              <span className="toolbar-session-kicker">Signed In</span>
              <strong className="toolbar-session-name">
                {user?.full_name ||
                  user?.email ||
                  user?.cr_number ||
                  user?.mt5_number ||
                  "Spike User"}
              </strong>
            </div>

            <button
              type="button"
              className="toolbar-session-logout"
              onClick={onLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div ref={containerRef} className="chart-container">
        {livePriceBadge ? (
          <div
            className="chart-live-price-badge"
            style={{
              top: `${livePriceBadge.top}px`,
              width: `${livePriceBadge.widthCh}ch`,
            }}
          >
            <div className="chart-live-price-line">{livePriceBadge.priceText}</div>
            <div className="chart-live-price-line">{livePriceBadge.countdownText}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
